/**
 * The App Gallery's own order, shared with the home page so the two never
 * disagree — see AppGalleryTab's Move up/down and App.tsx's `orderedModules`.
 */

import type { AppModule } from '../types';

/**
 * Sorts by `homeOrder`, lowest first. A module with none yet (never
 * explicitly reordered) sorts AFTER every module that has one, keeping its
 * original relative position among other unordered modules — `Array#sort`
 * is stable, so turning this on for the first time changes nothing until an
 * admin actually reorders something.
 */
export function sortByHomeOrder(modules: AppModule[]): AppModule[] {
  return [...modules].sort((a, b) => {
    const orderA = a.homeOrder ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.homeOrder ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
}

/**
 * Moves one module up or down within its ordered list, and returns the
 * complete new order with every position renumbered 0..N-1 — not just the
 * two swapped entries, because any entry that never had an explicit
 * `homeOrder` needs one assigned the first time anything in the list moves,
 * or its position relative to the swap would be ambiguous.
 *
 * Returns null when the move is impossible (already at that end, or the
 * module is not in the list) — nothing to persist.
 */
export function moveModule(
  ordered: AppModule[],
  moduleId: string,
  direction: 'up' | 'down',
): { id: string; homeOrder: number }[] | null {
  const index = ordered.findIndex((module) => module.id === moduleId);
  if (index === -1) return null;
  const target = direction === 'up' ? index - 1 : index + 1;
  if (target < 0 || target >= ordered.length) return null;

  const reordered = [...ordered];
  [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

  return reordered.map((module, position) => ({ id: module.id, homeOrder: position }));
}
